import type { ContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import { useTraditionalCharactersEnabled } from "@-/dev.improve.haniverse.lib.posthog";
import {
  type CardReviewSettings,
  cardReviewSettingsFromUser,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  type DueUserCard,
  filterReviewEligibleDueUserCards,
  type NextDueUserCardData,
  pendingPinnedReviewCards,
  useDueUserCardsQueryOptions,
  useNextDueUserCardDataQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import {
  isLessonInUnstartedLessonGroup,
  useNextLessonQueryOptions,
  useNextLessonCandidateQueryOptions,
} from "@-/dev.improve.haniverse.lib.lesson.get";
import { LEARN_SUMMARY_WELCOME_SLIDE_SLUG } from "#learn-summary-welcome-slide-slug";
import { getQuizManifestEntryByKey } from "@-/dev.improve.haniverse.assets.slides";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { ReviewFocus } from "@-/dev.improve.haniverse.lib.learn.review-focus";
import type { StudyEnvironment } from "@-/dev.improve.haniverse.lib.learn.study-environment";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { usePronouncedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import {
  useDueRowsCountQueryOptions,
  useReviewRechargeQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  type DailyNewGraphemeAllowance,
  useDailyNewGraphemeAllowanceQueryOptions,
} from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import type {
  LearnSummaryContinueDestination,
  LearnSummaryDueReviewCard,
  LearnSummaryPageResolution,
  LearnSummaryReviewDrill,
} from "#learn-summary-page-resolution";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";

// The resolver fans out to next-due review cards and the next lesson (a slide
// or a new morpheme lesson, intermixed by level step). The table set is the
// UNION of every table the descendant queries read. The query-key getter
// registers the resolver against this list so the whole fan-out stays fresh.
// `user`/`collection` are read by the actor-user-data
// resolver (the `user` row now also carries the per-user settings), the rest
// by the review/lesson scans.
const LEARN_SUMMARY_TABLES = [
  "user",
  "user_card",
  "user_card_review",
  "fsrs_card",
  "user_grapheme_meaning_card",
  "user_grapheme_pronunciation_card",
  // The due-review scan resolves word cards too (the shared due-card read joins the word
  // overlay), so a change to one has to invalidate this fan-out. It was missing from this list,
  // which meant adding or rating a word card left the summary's due counts stale until something
  // else happened to invalidate them.
  "user_word_sentence_card",
  "user_slide",
  "collection",
] as const;

/**
 * Every due, review-eligible card, in earliest-due order — one entry per card, so a character with
 * both its meaning and pronunciation card due shows up twice (once per card type).
 *
 * Pure over the shared due-card scan: the subject comes from the card's actor overlay, so this
 * needs no content-DB read at all. An earlier version fetched an app card PER DUE CARD through the
 * query client (hundreds of cache entries and a batched content read per 64), which is why a
 * returning user with a big queue waited on this before the grid appeared.
 */
function getDueReviewCardList(
  dueUserCards: readonly DueUserCard[],
  cardReviewSettings: CardReviewSettings,
  queue: {
    contentAvailability: ContentAvailability;
    reviewFocus: ReviewFocus;
    studyEnvironment: StudyEnvironment;
  },
): LearnSummaryDueReviewCard[] {
  // A quiz has no glyph to tile: the grid shows the characters and words due.
  return filterReviewEligibleDueUserCards(dueUserCards, cardReviewSettings, queue).flatMap(
    ({ cardContentKind, subject, userCardId }) =>
      cardContentKind === "quiz" ? [] : [{ cardContentKind, subject: subject.text, userCardId }],
  );
}

function summaryReviewDrill(card: NextDueUserCardData): LearnSummaryReviewDrill | null {
  if (card.kind === "drill") {
    return { drillType: card.drillType, grapheme: card.grapheme, kind: "drill" };
  }
  // The due reads keep only quizzes this build has (`isCardContentAvailable`).
  const quiz = getQuizManifestEntryByKey(card.quizKey);
  return quiz === undefined ? null : { kind: "quiz", quizSlug: quiz.slug };
}

function reachedDailyNewGraphemeLimit({
  isFreeTierCap,
  isLimitReached,
  limit,
}: DailyNewGraphemeAllowance): LearnSummaryPageResolution["dailyNewGraphemeLimitReached"] {
  return isLimitReached ? { isFreeTierCap, limit } : null;
}

function useGetLearnSummaryPageQueryKey() {
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  const [introduceNewCardsEnabled] = useDeviceSetting("introduceNewCardsEnabled");
  const userSettingsQueryOptions = actorQueries(
    import.meta.location,
  ).user.findFirst.useQueryOptions({});
  const nextDueUserCardDataQueryOptions = useNextDueUserCardDataQueryOptions();
  const getNextLessonQueryOptions = useNextLessonQueryOptions();
  const nextLessonCandidateQueryOptions = useNextLessonCandidateQueryOptions();
  const reviewRechargeQueryOptions = useReviewRechargeQueryOptions();
  const [reviewRechargeNewCardsEnabled] = useDeviceSetting("reviewRechargeNewCardsEnabled");
  const dueRowsCountQueryOptions = useDueRowsCountQueryOptions();
  const dueUserCardsQueryOptions = useDueUserCardsQueryOptions();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();
  const pronouncedGraphemesQueryOptions = usePronouncedGraphemesQueryOptions();
  const dailyNewGraphemeAllowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(LEARN_SUMMARY_TABLES));

  return ({ validateContent = false }: { validateContent?: boolean } = {}) =>
    withTableChangeVersion([
      "learnSummaryPage",
      {
        dailyNewGraphemeAllowanceQueryOptions,
        dueRowsCountQueryOptions,
        dueUserCardsQueryOptions,
        getNextLessonQueryOptions,
        introduceNewCardsEnabled,
        nextDueUserCardDataQueryOptions,
        nextLessonCandidateQueryOptions,
        pronouncedGraphemesQueryOptions,
        reviewRechargeNewCardsEnabled,
        reviewRechargeQueryOptions,
        studiedGraphemesQueryOptions,
        traditionalCharactersEnabled,
        userSettingsQueryOptions,
        validateContent,
      },
    ] as const);
}

const [useLearnSummaryPageQueryOptions, learnSummaryPageQueryFn] = createUseQueryOptions(
  useGetLearnSummaryPageQueryKey,
  async ({
    client,
    queryKey: [
      _,
      {
        dailyNewGraphemeAllowanceQueryOptions,
        dueRowsCountQueryOptions,
        dueUserCardsQueryOptions,
        getNextLessonQueryOptions,
        introduceNewCardsEnabled,
        nextDueUserCardDataQueryOptions,
        nextLessonCandidateQueryOptions,
        pronouncedGraphemesQueryOptions,
        reviewRechargeNewCardsEnabled,
        reviewRechargeQueryOptions,
        studiedGraphemesQueryOptions,
        traditionalCharactersEnabled,
        userSettingsQueryOptions,
        validateContent,
      },
    ],
  }) => {
    // Cold entry reads only the local actor database and bundled curriculum. Counts and CTA
    // eligibility therefore do not wait for cloud content, decomposition, or sync. Candidate
    // availability is provisional: the mounted card preloads validateContent=true after paint
    // or on interaction, without gating Start. Only that result drives preview/navigation.
    // Both paths share the
    // same unit/settings logic and actor query entries.
    const [
      dueReviewCount,
      dueUserCards,
      userSettings,
      nextLesson,
      nextReviewCard,
      studiedGraphemes,
      reviewRecharge,
      dailyNewGraphemeAllowance,
      pronouncedGraphemes,
      pinnedReviewQueue,
    ] = await Promise.all([
      client.fetchQuery(dueRowsCountQueryOptions),
      client.fetchQuery(dueUserCardsQueryOptions),
      client.fetchQuery(userSettingsQueryOptions),
      validateContent
        ? client.fetchQuery(getNextLessonQueryOptions)
        : client.fetchQuery(nextLessonCandidateQueryOptions),
      validateContent ? client.fetchQuery(nextDueUserCardDataQueryOptions) : Promise.resolve(null),
      client.fetchQuery(studiedGraphemesQueryOptions),
      client.fetchQuery(reviewRechargeQueryOptions),
      client.fetchQuery(dailyNewGraphemeAllowanceQueryOptions),
      client.fetchQuery(pronouncedGraphemesQueryOptions),
      client.fetchQuery(nextDueUserCardDataQueryOptions.queryKey[2].pinnedReviewQueueQueryOptions),
    ]);

    // A local candidate decides only the CTA. The actual review and its ordering/content
    // are resolved authoritatively by the card's preparation path before any preview/navigation.
    const { contentAvailability, reviewFocus, studyEnvironment, voiceOnly } =
      nextDueUserCardDataQueryOptions.queryKey[2];
    // The Admin panel's hardcoded queue heads the review queue whether or not its cards are due, so
    // its unspent cards count as due here and the head is offered at once (see `isOverdueReview`).
    const pinnedCards = pendingPinnedReviewCards(pinnedReviewQueue);
    const pinnedUserCardIds = new Set(pinnedCards.map((card) => card.userCardId));
    const dueUserCardIds = new Set(dueUserCards.map((card) => card.userCardId));
    const queueCards =
      pinnedCards.length === 0
        ? dueUserCards
        : [
            ...pinnedCards,
            ...dueUserCards.filter((card) => !pinnedUserCardIds.has(card.userCardId)),
          ];
    const queueSettings = { contentAvailability, reviewFocus, studyEnvironment };
    const localReviewCandidate =
      filterReviewEligibleDueUserCards(
        queueCards,
        cardReviewSettingsFromUser(userSettings),
        queueSettings,
      ).find((card) => !voiceOnly || card.cardContentKind === "grapheme-meaning") ?? null;
    const reviewCandidate = validateContent ? nextReviewCard : localReviewCandidate;
    const dueReviewCardList = getDueReviewCardList(
      queueCards,
      cardReviewSettingsFromUser(userSettings),
      queueSettings,
    );
    const pinnedNotDueCount = filterReviewEligibleDueUserCards(
      pinnedCards,
      cardReviewSettingsFromUser(userSettings),
      queueSettings,
    ).filter((card) => !dueUserCardIds.has(card.userCardId)).length;
    // A review focus practices one review kind: it never introduces lessons, so it behaves like
    // "Introduce New Cards" off and offers no Continue into the curriculum.
    const reviewFocusActive = reviewFocus !== "all";

    // Lesson level indices are normalized across morphemes and slides. Resolve the user's active
    // catalogue once so boundary checks treat a Level 2 start slide exactly like its first
    // character; onboarding is `before` 丨 on grapheme level 0.
    const levels =
      traditionalCharactersEnabled && userSettings?.hanziScript === "traditional"
        ? getTraditionalLevels()
        : getSimplifiedLevels();

    // Fresh learners enter the authored welcome card; the deck owns its successor and completion.
    if (
      nextLesson !== null &&
      nextLesson.type === "slide" &&
      nextLesson.slug === LEARN_SUMMARY_WELCOME_SLIDE_SLUG
    ) {
      const continueDestination: LearnSummaryContinueDestination = {
        kind: "slide",
        slideSlug: LEARN_SUMMARY_WELCOME_SLIDE_SLUG,
      };
      return {
        continueDestination,
        dailyNewGraphemeLimitReached: reachedDailyNewGraphemeLimit(dailyNewGraphemeAllowance),
        dueReviewCardList,
        dueReviewCount,
        hasReviewCandidate: false,
        isNewUser: true,
        reviewDrill: null,
      };
    }

    // With introductions enabled, newly introduced cards wait until the unit ends, and new cards
    // take priority while the New Cards toggle is spending review recharge. Review-only sessions can
    // always resume their due queue.
    const OVERDUE_REVIEW_GRACE_MS = 5 * 60 * 1000;
    const atUnitBoundary =
      nextLesson === null ||
      isLessonInUnstartedLessonGroup({
        lesson: nextLesson,
        levels,
        pronouncedGraphemes,
        studiedGraphemes,
      });
    // The new character that drained review recharge paid for its whole unit, so while New Cards
    // is still on, a started unit keeps priority even at 0 — as it does in the deck.
    const isSpendingReviewRecharge =
      reviewRechargeNewCardsEnabled && (reviewRecharge > 0 || !atUnitBoundary);
    const isOverdueReview =
      reviewCandidate !== null &&
      ((reviewCandidate.userCardId !== null && pinnedUserCardIds.has(reviewCandidate.userCardId)) ||
        Date.now() - new Date(reviewCandidate.due).getTime() > OVERDUE_REVIEW_GRACE_MS);

    const shouldSurfaceReview =
      reviewCandidate !== null &&
      (reviewFocusActive ||
        !introduceNewCardsEnabled ||
        nextLesson === null ||
        (!isSpendingReviewRecharge && (atUnitBoundary || isOverdueReview)));
    // Only the content-validated path can expose a drill destination. The local path exposes
    // hasReviewCandidate for the CTA without pretending a missing/undrillable card is ready.
    const reviewDrill: LearnSummaryReviewDrill | null =
      shouldSurfaceReview && nextReviewCard !== null ? summaryReviewDrill(nextReviewCard) : null;

    const continueDestination: LearnSummaryContinueDestination | null =
      reviewFocusActive || nextLesson === null
        ? null
        : nextLesson.type === "slide"
          ? { kind: "slide", slideSlug: nextLesson.slug }
          : nextLesson.type === "quiz"
            ? { kind: "quiz", quizSlug: nextLesson.slug, source: "lesson" }
            : {
              kind: "newMorpheme",
              morpheme: nextLesson.graphemeCard.front,
              ...(nextLesson.part === undefined ? {} : { part: nextLesson.part }),
              ...(nextLesson.pinyin === undefined ? {} : { pinyin: nextLesson.pinyin }),
            };

    return {
      continueDestination,
      dailyNewGraphemeLimitReached: reachedDailyNewGraphemeLimit(dailyNewGraphemeAllowance),
      dueReviewCardList,
      dueReviewCount: dueReviewCount + pinnedNotDueCount,
      hasReviewCandidate: shouldSurfaceReview,
      isNewUser: false,
      reviewDrill,
    };
  },
);

export { learnSummaryPageQueryFn, useLearnSummaryPageQueryOptions };
